import { AxiosError } from "axios";
import { createApp } from "vue";
import * as Sentry from "@sentry/vue";
import PrimeVue from "primevue/config";
import ToastService from "primevue/toastservice";
import Tooltip from "primevue/tooltip";
import ConfirmationService from "primevue/confirmationservice";
import { VueSvgIconPlugin } from "@yzfe/vue3-svgicon";
import { createGtm } from "@gtm-support/vue-gtm";
import { isNavigationFailure } from "vue-router";

import App from "@/App.vue";
import router from "@/router";
import { pinia, useRootStore } from "@/stores";
import { useAccountStore } from "@/stores/account";
import dayjsPlugin from "@/plugins/dayjs";
import pageTitleMixin from "@/mixins/pageTitle";
import openAPIClientAxios from "@/plugins/openAPIClientAxios";

import "@yzfe/svgicon/lib/svgicon.css";
import "./assets/scss/gentwo-pro/main.scss";
import "./assets/css/tailwind.css";
import VueDOMPurifyHTML from "vue-dompurify-html";

const app = createApp(App)
  .use(openAPIClientAxios)
  .use(pinia)
  .use(dayjsPlugin)
  .use(PrimeVue, {
    theme: {
      options: {
        cssLayer: {
          name: "primevue",
        },
      },
    },
  })
  .use(ToastService)
  .use(ConfirmationService)
  .mixin(pageTitleMixin)
  .use(VueSvgIconPlugin, { tagName: "icon" })
  .use(VueDOMPurifyHTML);

app.directive("tooltip", Tooltip);

if (import.meta.env.VITE_APP_ENVIRONMENT === "staging") {
  app.use(
    createGtm({
      id: "GTM-NVJP4G9K",
      source: "https://gtagmanager.gentwo.com/ZOtQa9QvY/",
      defer: false,
      enabled: true,
      debug: true,
      loadScript: true,
      vueRouter: router,
    }),
  );
} else if (import.meta.env.PROD) {
  app.use(
    createGtm({
      id: "GTM-MDMXNG4",
      source: "https://gtagmanager.gentwo.com/2sISVwrn/",
      defer: false,
      enabled: true,
      debug: false,
      loadScript: true,
      vueRouter: router,
    }),
  );
  Sentry.init({
    app,
    dsn: "https://6d0579917c4046759d628e25e13e5dbf@o185975.ingest.sentry.io/1280961",
    ignoreErrors: ["ChunkLoadError"],
    release: import.meta.env.VITE_APP_VERSION,
    environment: import.meta.env.VITE_APP_ENVIRONMENT,
    attachStacktrace: true,
    integrations: [Sentry.browserTracingIntegration({ router })],
    beforeSend(event, hint) {
      // we do not want to log the 400, 401 and 403 to sentry because
      // - for the user they are handeled in api.js
      // - we do (miss)use 400 return from the MVCController as a valid return code
      const error: AxiosError = hint.originalException as AxiosError;

      // vue-router raises navigation control flow (guard redirects like
      // next("/login?..."), plus aborted/cancelled/duplicated navigations) as
      // internal empty-message Errors. They are not faults, so drop them before
      // the report dialog / Sentry reporting below. isNavigationFailure() with no
      // type argument matches every router error, NAVIGATION_GUARD_REDIRECT included.
      if (isNavigationFailure(hint.originalException)) {
        return null;
      }

      if (
        error?.message &&
        (error.message.match(/Network Error/i) ||
          error.message.match(/ChunkLoadError/i) ||
          error.message.includes("dynamically imported module") ||
          error.message.includes("module script failed") ||
          error.message.includes("Unable to preload CSS"))
      ) {
        return null;
      }

      if (
        error?.response?.status &&
        (error.response.status === 400 ||
          error.response.status === 401 ||
          error.response.status === 403 ||
          error.response.status === 409)
      ) {
        return null;
      }

      if (event.exception) {
        const account = useAccountStore(pinia);

        Sentry.showReportDialog({
          eventId: event.event_id,
          user: {
            name: account.user?.firstName + " " + account.user?.lastName,
            email: account.user?.email,
          },
          title: "Sorry, we've had a problem processing this",
          subtitle: "The Gentwo Development team has already been notified.",
          subtitle2:
            "If you'd like to help us further, please tell us what happened below. If this is impacting your business, please email dev@gentwo.com or call us on +41 44 512 75 00",
        });
      }
      return event;
    },
  });
}

const rootStore = useRootStore();
rootStore
  .init()
  .then(() => {
    // because we need to first load the root store and only then load the router
    // this prevent race conditions where router checks for authentication before
    // the accountStore has time to get the user details
    app.use(router);

    app.mount("#app");
  })
  .catch(e => {
    // normalize the exception value to make sure it's an Error object
    if (!(e instanceof Error)) {
      e = new Error(e);
    }
    if (e.name === "AxiosError") {
      const axiosError: AxiosError = e as AxiosError;
      if (axiosError.response?.status === 401) {
        let returnUrl = "/";
        if (axiosError.request?.responseURL) {
          returnUrl = new URL(axiosError.request.responseURL).pathname;
        }

        window.location.href = `/apptwo/account/login?return_url=${returnUrl}`;
        return;
      } else if (axiosError.response?.status) {
        window.location.href = `/app-down.html?status=${axiosError.response?.status}`;
        return;
      }

      window.location.href = `/app-down.html`;
    }
  });
